/**
 * Streaming chat hook — the send path of a conversation, against
 * tricorder-session through the gateway proxy from the cut-over on.
 *
 * POST /sessions/{id}/messages {content} answers 202 with the persisted user
 * message; the agent's answer arrives on the session's own event stream
 * (GET /sessions/{id}/events — useSessionEventStream drives it): turn.delta
 * text appended to the placeholder this hook pre-allocated in the cache,
 * turn.completed naming the answer's id (the catch-up read refetches it),
 * turn.failed dropping the placeholder and surfacing the reason. A turn that
 * failed terminally leaves a durable turn_failure record in the transcript
 * (SPEC.md section 7), so the failure survives a reload.
 *
 * Images and documents died with the cut-over (the re-created store has no
 * image columns); the send is text.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { sessionKeys, type SessionDetail } from './useSessions';
import { createLogger } from '@/utils/logger';
import { generateTempMessageId, abortableDelay } from '../lib/streamReader';
import { randomUUID } from '@/lib/uuid';
import { RETRY_PRE_SEND_DELAY_MS } from '@/lib/constants';
import { sanitizeErrorMessage } from '../utils/errorMessages';
import { logInvalidateQueriesError } from '../lib/error-utils';
import { turnKey, usePendingTurnsStore } from '@/stores/pendingTurns';
import { sessionsApi } from '../api/endpoints/sessions';
import {
  CONVERSATION_MESSAGE_TYPE,
  type MessageResponse,
} from '../api/types_session';
import type { CitationResponse as CitationWire } from '../api/types';

const logger = createLogger('StreamingChat');

/**
 * Streaming state for chat sessions.
 */
export interface StreamingState {
  isStreaming: boolean;
  /** Never appended to from the cut-over on: turn deltas land on the cache
   *  placeholder (useSessionEventStream), so this stays '' — the streaming
   *  fallback bubble renders its thinking state from isStreaming. */
  content: string;
  citations: CitationWire[];
  error: string | null;
  userMessageId: string | null;
  assistantMessageId: string | null;
  /** The seq of the in-flight turn (the user's message seq — every turn event names it). */
  pendingSeq: number | null;
  isRetrying: boolean;
}

const initialState: StreamingState = {
  isStreaming: false,
  content: '',
  citations: [],
  error: null,
  userMessageId: null,
  assistantMessageId: null,
  pendingSeq: null,
  isRetrying: false,
};

/**
 * Hook for streaming chat messages.
 */
export function useStreamingChat(sessionId: string) {
  const queryClient = useQueryClient();
  const [state, setState] = useState(initialState);
  const lastMessageRef = useRef<{
    content: string;
    // Stable across retries of THIS message so a resend dedups to the same
    // turn server-side.
    idempotencyKey: string;
  } | null>(null);
  // The retry settle's abort handle: cancel() during the pre-send pause
  // suppresses the resend (the nurse changed her mind — the message never
  // reached the server, so nothing needs to stay in flight).
  const retryAbortRef = useRef<AbortController | null>(null);

  // Reset state when sessionId changes.
  useEffect(() => {
    setState(initialState);
    lastMessageRef.current = null;
    return () => {
      lastMessageRef.current = null;
    };
  }, [sessionId, queryClient]);

  const sendMessage = useCallback(
    async (
      content: string,
      options?: {
        // Carried by retry() to keep the send idempotent across attempts.
        idempotencyKey?: string;
      },
    ) => {
      const idempotencyKey = options?.idempotencyKey ?? randomUUID();
      lastMessageRef.current = { content, idempotencyKey };

      setState({
        ...initialState,
        isStreaming: true,
      });

      // Optimistic user message (replaced by the persisted row the 202
      // returns — same shape, so the swap is invisible).
      const tempMessageId = generateTempMessageId();
      const cacheKey = sessionKeys.detail(sessionId);
      const previousSession = queryClient.getQueryData<SessionDetail>(cacheKey);
      if (previousSession) {
        const optimisticUserMessage: MessageResponse = {
          id: tempMessageId,
          session: sessionId,
          seq: previousSession.messages.items.length + 1,
          type: CONVERSATION_MESSAGE_TYPE,
          role: 'user',
          author: { performer: '' },
          payload: [{ type: 'text', text: { text: content } }],
          created_at: new Date().toISOString(),
        };
        queryClient.setQueryData<SessionDetail>(cacheKey, {
          ...previousSession,
          messages: {
            ...previousSession.messages,
            items: [...previousSession.messages.items, optimisticUserMessage],
          },
        });
      }

      let accepted = false;
      try {
        const acceptedMessage = await sessionsApi.postMessage(sessionId, content, idempotencyKey);
        accepted = true;

        // Swap the optimistic row for the persisted one and register the turn:
        // events name the seq of the user's message, and the placeholder id is
        // client-side — turn.completed replaces it with the agent's message.
        const current = queryClient.getQueryData<SessionDetail>(cacheKey);
        const placeholderId = `pending-${sessionId}-${acceptedMessage.seq}`;
        if (current) {
          const items = current.messages.items.map((m) =>
            m.id === tempMessageId ? acceptedMessage : m,
          );
          const placeholder: MessageResponse = {
            id: placeholderId,
            session: sessionId,
            seq: acceptedMessage.seq + 1,
            type: CONVERSATION_MESSAGE_TYPE,
            role: 'agent',
            author: { performer: '' },
            payload: [],
            created_at: new Date().toISOString(),
          };
          queryClient.setQueryData<SessionDetail>(cacheKey, {
            ...current,
            messages: { ...current.messages, items: [...items, placeholder] },
          });
        }
        usePendingTurnsStore.getState().registerTurn({
          turnId: turnKey(sessionId, acceptedMessage.seq),
          sessionId,
          seq: acceptedMessage.seq,
          assistantMessageId: placeholderId,
        });
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          userMessageId: acceptedMessage.id,
          assistantMessageId: placeholderId,
          pendingSeq: acceptedMessage.seq,
        }));
        return;
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? sanitizeErrorMessage(error.message)
            : sanitizeErrorMessage('Streaming failed');

        // Roll back the optimistic user message only when the send never
        // reached the server. If the server already accepted it, the message
        // is durably stored and stays.
        if (!accepted) {
          const current = queryClient.getQueryData<SessionDetail>(cacheKey);
          if (current) {
            const reconciled = current.messages.items.filter((m) => m.id !== tempMessageId);
            if (reconciled.length !== current.messages.items.length) {
              queryClient.setQueryData<SessionDetail>(cacheKey, {
                ...current,
                messages: { ...current.messages, items: reconciled },
              });
            }
          }
        }
        void queryClient
          .invalidateQueries({ queryKey: cacheKey })
          .catch((err) => logInvalidateQueriesError('useStreamingChat', err));

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: errorMessage,
        }));
      }
    },
    [sessionId, queryClient],
  );

  /**
   * Retry the last failed message.
   */
  const retry = useCallback(async () => {
    if (!lastMessageRef.current) return;
    if (state.isStreaming || state.isRetrying) return;

    const { content, idempotencyKey } = lastMessageRef.current;

    setState((prev) => ({
      ...prev,
      isRetrying: true,
      error: null,
    }));

    const controller = new AbortController();
    retryAbortRef.current = controller;
    try {
      // abortableDelay RESOLVES on abort — the aborted check below is the
      // suppression, not the await.
      await abortableDelay(RETRY_PRE_SEND_DELAY_MS, controller.signal);
      if (controller.signal.aborted) return;
      await sendMessage(content, { idempotencyKey });
    } catch (error) {
      if (controller.signal.aborted) return;
      logger.error('Retry failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      retryAbortRef.current = null;
      setState((prev) => ({
        ...prev,
        isRetrying: false,
      }));
    }
  }, [sendMessage, state.isStreaming, state.isRetrying]);

  /**
   * Cancel the current stream — the send is already durable; this only clears
   * the client-side waiting state.
   */
  const cancel = useCallback(() => {
    retryAbortRef.current?.abort();
    setState((prev) => ({
      ...prev,
      isStreaming: false,
      isRetrying: false,
      error: null,
    }));
  }, []);

  /**
   * Reset the streaming state.
   */
  const reset = useCallback(() => {
    setState(initialState);
    lastMessageRef.current = null;
  }, []);

  return {
    ...state,
    sendMessage,
    retry,
    cancel,
    reset,
  };
}

/**
 * Wrapper for regular chat (drop-in replacement for useStreamingMessage).
 */
export function useStreamingMessage(sessionId: string) {
  return useStreamingChat(sessionId);
}
